import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../hooks';
import './privacy.css';

const STORAGE_KEY = 'cogeneration_privacy_consent';
const CONSENT_VERSION = '2026-08-26';
const GA_MEASUREMENT_ID = 'G-69TWM5VSHK';

const defaults = { necessary: true, analytics: false, maps: false };

const copy = {
  ru: {
    title: 'Настройки конфиденциальности',
    summary:
      'Мы используем обязательное хранилище для языка и настроек конфиденциальности. Аналитика и карта Google включаются только с вашего разрешения.',
    accept: 'Принять все',
    reject: 'Отклонить необязательные',
    customize: 'Настроить',
    save: 'Сохранить настройки',
    close: 'Закрыть',
    necessary: 'Обязательные',
    necessaryHelp: 'Нужны для языка, безопасности и сохранения вашего выбора. Всегда включены.',
    analytics: 'Аналитика',
    analyticsHelp: 'Помогает нам понимать использование сайта через Google Analytics.',
    maps: 'Карта Google',
    mapsHelp: 'Разрешает загрузку интерактивной карты Google на странице контактов.',
    policy: 'Политика конфиденциальности',
  },
  md: {
    title: 'Preferințe de confidențialitate',
    summary:
      'Folosim stocare strict necesară pentru limbă și preferințele de confidențialitate. Analitica și harta Google se activează numai cu permisiunea dvs.',
    accept: 'Acceptă toate',
    reject: 'Respinge opționalele',
    customize: 'Personalizează',
    save: 'Salvează preferințele',
    close: 'Închide',
    necessary: 'Strict necesare',
    necessaryHelp: 'Necesare pentru limbă, securitate și memorarea alegerii. Sunt mereu active.',
    analytics: 'Analitică',
    analyticsHelp: 'Ne ajută să înțelegem utilizarea site-ului prin Google Analytics.',
    maps: 'Harta Google',
    mapsHelp: 'Permite încărcarea hărții interactive Google în secțiunea de contact.',
    policy: 'Politica de confidențialitate',
  },
  en: {
    title: 'Privacy preferences',
    summary:
      'We use strictly necessary storage for language and privacy preferences. Analytics and Google Maps load only with your permission.',
    accept: 'Accept all',
    reject: 'Reject optional',
    customize: 'Customize',
    save: 'Save preferences',
    close: 'Close',
    necessary: 'Strictly necessary',
    necessaryHelp: 'Required for language, security, and remembering your choice. Always active.',
    analytics: 'Analytics',
    analyticsHelp: 'Helps us understand site use through Google Analytics.',
    maps: 'Google Maps',
    mapsHelp: 'Allows the interactive Google map to load in the contact section.',
    policy: 'Privacy policy',
  },
};

const PrivacyContext = createContext(null);

const readConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (stored?.version !== CONSENT_VERSION) return null;
    return { ...defaults, ...stored.preferences };
  } catch {
    return null;
  }
};

const expireAnalyticsCookies = () => {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_')) {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax; Secure`;
    }
  });
};

const updateAnalytics = (enabled) => {
  if (!enabled) {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
    expireAnalyticsCookies();
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (!document.getElementById('google-analytics-script')) {
    const script = document.createElement('script');
    script.id = 'google-analytics-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_flags: 'SameSite=Lax;Secure',
  });
};

export const PrivacyConsentProvider = ({ children }) => {
  const initial = readConsent();
  const [preferences, setPreferences] = useState(initial || defaults);
  const [hasChoice, setHasChoice] = useState(Boolean(initial));
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const savePreferences = (next) => {
    const normalized = { ...defaults, ...next, necessary: true };
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: CONSENT_VERSION,
        updatedAt: new Date().toISOString(),
        preferences: normalized,
      })
    );
    setPreferences(normalized);
    setHasChoice(true);
    setPreferencesOpen(false);
  };

  useEffect(() => {
    updateAnalytics(hasChoice && preferences.analytics);
  }, [hasChoice, preferences.analytics]);

  const value = useMemo(
    () => ({
      preferences,
      hasChoice,
      savePreferences,
      openPreferences: () => setPreferencesOpen(true),
    }),
    [preferences, hasChoice]
  );

  return (
    <PrivacyContext.Provider value={value}>
      {children}
      <PrivacyControls
        hasChoice={hasChoice}
        preferences={preferences}
        open={preferencesOpen}
        onOpen={() => setPreferencesOpen(true)}
        onClose={() => setPreferencesOpen(false)}
        onSave={savePreferences}
      />
    </PrivacyContext.Provider>
  );
};

export const usePrivacyConsent = () => {
  const context = useContext(PrivacyContext);
  if (!context) throw new Error('usePrivacyConsent must be used within PrivacyConsentProvider');
  return context;
};

const PrivacyControls = ({ hasChoice, preferences, open, onOpen, onClose, onSave }) => {
  const { lang } = useLanguage();
  const text = copy[lang] || copy.md;
  const [draft, setDraft] = useState(preferences);

  useEffect(() => setDraft(preferences), [preferences, open]);

  return (
    <>
      {!hasChoice && (
        <section className="privacy-banner" aria-label={text.title}>
          <div>
            <h2>{text.title}</h2>
            <p>{text.summary}</p>
            <a href="/privacy">{text.policy}</a>
          </div>
          <div className="privacy-actions">
            <button className="privacy-button secondary" onClick={() => onSave(defaults)}>
              {text.reject}
            </button>
            <button className="privacy-button secondary" onClick={onOpen}>
              {text.customize}
            </button>
            <button
              className="privacy-button primary"
              onClick={() => onSave({ necessary: true, analytics: true, maps: true })}
            >
              {text.accept}
            </button>
          </div>
        </section>
      )}

      {open && (
        <div className="privacy-overlay" role="presentation">
          <section
            className="privacy-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-title"
          >
            <button className="privacy-close" aria-label={text.close} onClick={onClose}>
              ×
            </button>
            <h2 id="privacy-title">{text.title}</h2>
            <p>{text.summary}</p>
            <PreferenceRow label={text.necessary} help={text.necessaryHelp} checked disabled />
            <PreferenceRow
              label={text.analytics}
              help={text.analyticsHelp}
              checked={draft.analytics}
              onChange={(checked) => setDraft({ ...draft, analytics: checked })}
            />
            <PreferenceRow
              label={text.maps}
              help={text.mapsHelp}
              checked={draft.maps}
              onChange={(checked) => setDraft({ ...draft, maps: checked })}
            />
            <div className="privacy-actions">
              <button className="privacy-button secondary" onClick={() => onSave(defaults)}>
                {text.reject}
              </button>
              <button className="privacy-button primary" onClick={() => onSave(draft)}>
                {text.save}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

const PreferenceRow = ({ label, help, checked, disabled = false, onChange }) => (
  <label className="privacy-preference">
    <span>
      <strong>{label}</strong>
      <small>{help}</small>
    </span>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange?.(event.target.checked)}
    />
  </label>
);

export { CONSENT_VERSION };
